// src/pages/nfl/NflSimulations.jsx
import React, { useEffect, useMemo, useState } from "react";
/** ===== CONFIG ===== */
const BASE = "/data/nfl/latest";
const FILES = {
  player: `${BASE}/player_sim_results.json`,
  team: `${BASE}/team_sim_results.json`,
  game: `${BASE}/game_sim_results.json`,
};
function cx(...classes) {
  return classes.filter(Boolean).join(" ");
}
/** ===== HELPERS ===== */
async function fetchJson(url) {
  const res = await fetch(`${url}?_=${Date.now()}`, { cache: "no-store" });
  if (!res.ok) throw new Error(`HTTP ${res.status} @ ${url}`);
  const txt = await res.text();
  const ct = res.headers.get("content-type") || "";
  const looksJson = ct.includes("application/json") || /^[\[{]/.test(txt.trim());
  if (!looksJson) throw new Error(`Not JSON @ ${url}`);
  try {
    return JSON.parse(txt);
  } catch {
    throw new Error(`Invalid JSON @ ${url}`);
  }
}
function isNum(v) {
  return typeof v === "number" && Number.isFinite(v);
}
function toNum(v) {
  if (v == null || v === "") return null;
  if (isNum(v)) return v;
  const n = Number(String(v).replace(/[,%$\s]/g, ""));
  return Number.isFinite(n) ? n : null;
}
function fmt1(v) {
  if (v == null || v === "") return "";
  if (typeof v === "boolean") return v ? "Yes" : "No";
  if (isNum(v)) return Number(v).toFixed(1);
  return String(v);
}
function fmt2(v) {
  if (v == null || v === "") return "";
  if (typeof v === "boolean") return v ? "Yes" : "No";
  if (isNum(v)) return Number(v).toFixed(2);
  return String(v);
}
function toPct(v) {
  if (!isNum(v)) return "";
  const p = v <= 1.0000001 ? v : v / 100;
  const pct = p * 100;
  const r = Math.round(pct * 10) / 10;
  return `${r.toFixed(1)}%`;
}
/**
 * Fair American odds from probability p (0..1).
 * p >= 0.5 => negative odds
 * p < 0.5  => positive odds
 */
function probToAmericanOdds(p) {
  if (!isNum(p)) return "";
  if (p <= 0) return "";
  if (p >= 1) return "-∞";
  const prob = p <= 1.0000001 ? p : p / 100;
  if (prob >= 0.5) {
    const odds = -Math.round((prob / (1 - prob)) * 100);
    return String(odds);
  } else {
    const odds = Math.round(((1 - prob) / prob) * 100);
    return `+${odds}`;
  }
}
function exportToCsv(filename, rows, columns) {
  const header = columns.join(",");
  const body = rows
    .map((row) =>
      columns
        .map((col) => {
          const raw = row[col];
          const cell = raw == null ? "" : String(raw);
          return '"' + cell.replaceAll('"', '""') + '"';
        })
        .join(",")
    )
    .join("\n");
  const blob = new Blob([header + "\n" + body], {
    type: "text/csv;charset=utf-8;",
  });
  const link = document.createElement("a");
  const url = URL.createObjectURL(blob);
  link.href = url;
  link.setAttribute("download", filename);
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}
function getColumns(rows) {
  if (!rows?.length) return [];
  const set = new Set();
  for (const r of rows) {
    if (!r) continue;
    Object.keys(r).forEach((k) => set.add(k));
  }
  return Array.from(set);
}
function orderColumns(cols, primaryKey) {
  const set = new Set(cols);
  const head = [primaryKey].filter((c) => set.has(c));
  const tail = cols.filter((c) => !head.includes(c));
  return [...head, ...tail];
}
function pickPrimaryKey(rows, kind) {
  if (!rows?.length) return "Name";
  const keys = Object.keys(rows[0] || {});
  const candidatesCommon = ["Player", "player", "Name", "name"];
  const candidatesTeam = ["Team", "team", "Tm", "tm"];
  const candidatesGame = ["Game", "game", "Matchup", "matchup", "Away", "Home", "away", "home"];
  const list =
    kind === "team"
      ? [...candidatesTeam, ...candidatesCommon]
      : kind === "game"
      ? [...candidatesGame, ...candidatesCommon]
      : candidatesCommon;
  for (const c of list) if (keys.includes(c)) return c;
  return (
    keys.find((k) => k.toLowerCase().includes("player")) ||
    keys.find((k) => k.toLowerCase().includes("team")) ||
    keys[0] ||
    "Name"
  );
}
function guessTeamKeyFromRow(row) {
  if (!row) return null;
  const keys = Object.keys(row);
  const candidates = ["Team", "team", "Tm", "tm", "TEAM", "TeamAbbrev", "teamabbrev"];
  for (const c of candidates) if (keys.includes(c)) return c;
  const found = keys.find((k) => k.toLowerCase().includes("team"));
  return found || null;
}
function guessOppKeyFromRow(row) {
  if (!row) return null;
  const keys = Object.keys(row);
  const candidates = ["Opp", "opp", "Opponent", "opponent", "OPP", "OppTeam", "oppTeam"];
  for (const c of candidates) if (keys.includes(c)) return c;
  return keys.find((k) => k.toLowerCase() === "opp" || k.toLowerCase().includes("opponent")) || null;
}
function guessSlotKeyFromRow(row) {
  if (!row) return null;
  const keys = Object.keys(row);
  const candidates = ["Slot", "slot", "SLOT"];
  for (const c of candidates) if (keys.includes(c)) return c;
  return keys.find((k) => k.toLowerCase() === "slot") || null;
}
/** force these cols immediately after primaryKey (if they exist) */
function pinAfterPrimary(cols, primaryKey, pinnedKeys) {
  const pins = (pinnedKeys || []).filter(Boolean);
  const set = new Set(cols);
  const orderedPins = pins.filter((k) => set.has(k) && k !== primaryKey);
  const rest = cols.filter((c) => c !== primaryKey && !orderedPins.includes(c));
  return [primaryKey, ...orderedPins, ...rest];
}
function isOutcomeCol(col) {
  // outcomes appear like P(PassYd>=250), P(RecYd>=50), etc
  return /^P\(/.test(col) || String(col).toLowerCase().startsWith("p(");
}
function onlyGteOutcomeCols(cols) {
  const outcomes = cols.filter(isOutcomeCol);
  return outcomes.filter((c) => String(c).includes(">=") || String(c).includes("≥"));
}
function prettyOutcomeLabel(col) {
  // "P(PassYd>=250)" -> "250+ PASS YD"
  const m = String(col || "").match(/^P\(\s*([A-Za-z0-9 _%\/.-]+)\s*(?:>=|≥)\s*([0-9.]+)\s*\)$/i);
  if (!m) return col;
  const stat = String(m[1] || "")
    .trim()
    .replaceAll("_", " ")
    .toUpperCase();
  const n = String(m[2] || "").trim();
  return `${n}+ ${stat}`;
}
/** projection header prettifier (display-only) */
function prettyProjectionLabel(col) {
  const raw = String(col || "");
  // --- outcome-style cols (if any sneak into projections) ---
  if (/^P\(/i.test(raw)) return prettyOutcomeLabel(raw);
  // --- existing DK/FD prettifier ---
  if (raw === "DK_P10") return "DK Floor";
  if (raw === "DK_P90") return "DK Ceiling";
  if (raw === "FD_P10") return "FD Floor";
  if (raw === "FD_P90") return "FD Ceiling";
  if (raw === "DK_Mean") return "DK Mean";
  if (raw === "DK_Std") return "DK Std";
  if (raw === "FD_Mean") return "FD Mean";
  if (raw === "FD_Std") return "FD Std";
  // DK_P(15+) -> DK 15+
  const dkfd = raw.match(/^(DK|FD)_P\((.+)\)$/i);
  if (dkfd) return `${dkfd[1].toUpperCase()} ${dkfd[2]}`;
  // ----- base stat pretty helper -----
  const map = {
    // Passing
    PassAtt: "Pass Att",
    PassYd: "Pass Yds",
    PassYds: "Pass Yds",
    PassTD: "Pass TD",
    Int: "INT",
    INT: "INT",
    Completions: "Completions",
    PassYard: "Pass Yds",
    // Rushing
    RushAtt: "Rush Att",
    RushYd: "Rush Yds",
    RushYds: "Rush Yds",
    RushTD: "Rush TD",
    // Receiving
    Targets: "Targets",
    TargetShare: "Target Share",
    TargetShare_Pct: "Target Share %",
    Receptions: "Receptions",
    RecYd: "Rec Yds",
    RecYds: "Rec Yds",
    RecTD: "Rec TD",
    // Kicker
    XP_Att: "XP Att",
    XP_Made: "XP Made",
    FG_Att: "FG Att",
    FG_Made: "FG Made",
    FG0_39_Made: "FG 0-39 Made",
    FG40_49_Made: "FG 40-49 Made",
    FG50p_Made: "FG 50+ Made",
    // Fumbles
    FumLost: "Fum Lost",
    // Defense / DST
    DST_Sacks: "DST Sacks",
    DST_INT: "DST INT",
    DST_FumRec: "DST Fum Rec",
    DST_DefTD: "DST Def TD",
    DST_PointsAllowed: "DST Pts Allowed",
    // Misc
    Drives: "Drives",
    Plays: "Plays",
    Points: "Points",
    Sacks: "Sacks",
    TD: "TD",
  };
  const prettyBase = (base) => {
    // 1) direct map hit
    if (map[base]) return map[base];
    // 2) normalize underscores + camelCase spacing
    let s = String(base)
      .replaceAll("_", " ")
      .replace(/([a-z])([A-Z])/g, "$1 $2")
      .trim();
    // 3) tighten up common patterns
    s = s
      .replace(/\bYd\b/gi, "Yds")
      .replace(/\bYds\b/gi, "Yds")
      .replace(/\bTd\b/gi, "TD")
      .replace(/\bInt\b/gi, "INT")
      .replace(/\bXp\b/g, "XP")
      .replace(/\bFg\b/g, "FG")
      .replace(/\bDst\b/g, "DST");
    return s;
  };
  // --- generic stat percentiles/summary: *_P10, *_P90, *_Mean, *_Std ---
  let m = raw.match(/^(.+)_P10$/i);
  if (m) return `${prettyBase(m[1])} Floor`;
  m = raw.match(/^(.+)_P90$/i);
  if (m) return `${prettyBase(m[1])} Ceiling`;
  m = raw.match(/^(.+)_Mean$/i);
  if (m) return `${prettyBase(m[1])} Mean`;
  m = raw.match(/^(.+)_Std$/i);
  if (m) return `${prettyBase(m[1])} Std`;
  // fallback: just prettify whatever it is
  return prettyBase(raw);
}
function isProjectionProbCol(col) {
  // DK_P(15+), FD_P(20+), etc
  return /^(DK|FD)_P\(.+\)$/i.test(String(col));
}
/** IMPORTANT: treat numeric strings as data so we don't drop projection cols */
function hasAnyData(rows, col, primaryKey) {
  for (const r of rows) {
    if (col === primaryKey) continue;
    const v = r?.[col];
    if (v == null || v === "") continue;
    const n = toNum(v);
    if (n != null && Number.isFinite(n)) {
      if (Math.abs(n) > 1e-12) return true;
      continue;
    }
    return true;
  }
  return false;
}
/**
 * Projections view:
 * - Remove outcome columns entirely
 * - PLUS keep any non-outcome columns that contain numeric data (including numeric strings)
 * - Orders known blocks (DK/FD, Pass/Rush/Rec, K, DST, Team context, etc)
 */
function defaultProjectionCols(cols, rows, primaryKey) {
  // Remove outcome columns + (optional) any specific junk columns you never want
  const nonOutcome = cols.filter((c) => !isOutcomeCol(c) && c !== "TeamPassAtt_Mean");
  const rank = (c) => {
    const s = String(c);
    // Identity first
    if (s === primaryKey) return 0;
    if (/^(Team|team|Tm|tm)$/.test(s)) return 1;
    if (/^(Slot|slot|Pos|pos|Position|position)$/.test(s)) return 2;
    if (/^(Opp|opp|Opponent|opponent)$/.test(s)) return 3;
    // DK/FD blocks
    if (/^DK_/i.test(s)) return 10;
    if (/^FD_/i.test(s)) return 20;
    // Passing
    if (s === "PassAtt") return 30;
    if (s === "PassYd" || s === "PassYds") return 31;
    if (s === "PassTD") return 32;
    if (s === "Int" || s === "INT") return 33;
    // Rushing
    if (s === "RushAtt") return 40;
    if (s === "RushYd" || s === "RushYds") return 41;
    if (s === "RushTD") return 42;
    // Receiving
    if (s === "Targets") return 50;
    if (s === "TargetShare") return 51;
    if (s === "TargetShare_Pct") return 52;
    if (s === "Receptions") return 53;
    if (s === "RecYd" || s === "RecYds") return 54;
    if (s === "RecTD") return 55;
    // Kicker
    if (s === "XP_Att") return 56;
    if (s === "XP_Made") return 57;
    if (s === "FG_Att") return 58;
    if (s === "FG_Made") return 59;
    if (s === "FG0_39_Made") return 60;
    if (s === "FG40_49_Made") return 61;
    if (s === "FG50p_Made") return 62;
    // Defense / DST
    if (s === "DST_Sacks") return 63;
    if (s === "DST_INT") return 64;
    if (s === "DST_FumRec") return 65;
    if (s === "DST_DefTD") return 66;
    if (s === "DST_PointsAllowed") return 67;
    // Team context
    if (s.startsWith("Team")) return 70;
    // Other numeric stats
    return 100;
  };
  // Keep only columns that actually have numeric data (or are identity/context)
  const filtered = nonOutcome.filter((c) => {
    if (c === primaryKey) return true;
    // Always keep common context if present
    if (/^(Team|team|Slot|slot|Pos|pos|Position|position|Opp|opp|Opponent|opponent)$/.test(String(c))) return true;
    // Keep if any numeric data exists (numeric strings count)
    for (const r of rows || []) {
      const n = toNum(r?.[c]);
      if (n != null && Number.isFinite(n)) return true;
    }
    return false;
  });
  // Stable sort by rank, then alpha
  return filtered.sort((a, b) => {
    const ra = rank(a);
    const rb = rank(b);
    if (ra !== rb) return ra - rb;
    return String(a).localeCompare(String(b));
  });
}
/** projection chips */
function projectionBuckets(cols, primaryKey) {
  const buckets = [];
  const add = (key, label, pred) => {
    const list = cols.filter((c) => c !== primaryKey && pred(String(c)));
    if (list.length) buckets.push({ key, label, cols: list });
  };
  // All tab
  const all = cols.filter((c) => c !== primaryKey);
  if (all.length) buckets.push({ key: "ALL", label: "All", cols: all });
  // Stats tab (Pass/Rush/Rec + K + DST)
  add("STATS", "Stats", (c) =>
    /^(PassAtt|PassYd|PassYds|PassTD|Int|INT|RushAtt|RushYd|RushYds|RushTD|Targets|Receptions|RecYd|RecYds|RecTD|XP_Att|XP_Made|FG_Att|FG_Made|FG0_39_Made|FG40_49_Made|FG50p_Made|DST_Sacks|DST_INT|DST_FumRec|DST_DefTD|DST_PointsAllowed)$/i.test(
      c
    )
  );
  add("FANTASY", "Fantasy", (c) => /^DK_|^FD_/i.test(c));
  add("PASS", "Passing", (c) => /PassAtt|PassYd|PassYds|PassTD|Int|INT/i.test(c));
  add("RUSH", "Rushing", (c) => /RushAtt|RushYd|RushYds|RushTD/i.test(c));
  add("REC", "Receiving", (c) => /Targets|TargetShare|TargetShare_Pct|Receptions|RecYd|RecYds|RecTD/i.test(c));
  // NEW: Kicker + Defense chips
  add("K", "Kicker", (c) =>
    /^(XP_Att|XP_Made|FG_Att|FG_Made|FG0_39_Made|FG40_49_Made|FG50p_Made)$/i.test(c)
  );
  add("DST", "Defense", (c) =>
    /^(DST_Sacks|DST_INT|DST_FumRec|DST_DefTD|DST_PointsAllowed)$/i.test(c)
  );
  return buckets;
}
function metricBucketsForOutcomes(kind, gteCols) {
  const buckets = [];
  const add = (key, label, matcher) => {
    const cols = gteCols.filter((c) => matcher(String(c)));
    if (cols.length) buckets.push({ key, label, cols });
  };
  // Passing
  add("P_ATT", "P ATT", (c) => /PASS\s*ATT|PASSATT|PASS\s*ATTEMPT|ATT\s*PASS|COMPLET/i.test(c));
  add("P_YDS", "P YDS", (c) => /PASS\s*YD|PASSYD|PASS\s*YARDS|YDS\s*PASS|PASSING\s*YARDS/i.test(c));
  add("P_TD", "P TD", (c) => /PASS\s*TD|PASSTD|TD\s*PASS|PASSING\s*TD/i.test(c));
  // Rushing
  add("R_ATT", "R ATT", (c) => /RUSH\s*ATT|RUSHATT|RUSH\s*ATTEMPT|ATT\s*RUSH/i.test(c));
  add("R_YDS", "R YDS", (c) => /RUSH\s*YD|RUSHYD|RUSH\s*YARDS|YDS\s*RUSH|RUSHING\s*YARDS/i.test(c));
  add("R_TD", "R TD", (c) => /RUSH\s*TD|RUSHTD|TD\s*RUSH|RUSHING\s*TD/i.test(c));
  // Receiving
  add("TGT", "TGT", (c) => /\bTGT\b|TARGETS?/i.test(c));
  // Receptions ONLY (exclude targets, yards, TD)
  add("REC", "REC", (c) => {
    const s = String(c);
    const isRec = /\bREC\b|RECEPTIONS?/i.test(s);
    const isTargets = /\bTGT\b|TARGETS?/i.test(s);
    const isYards = /REC\s*YD|RECYD|REC\s*YARDS|YDS\s*REC|RECEIVING\s*YARDS/i.test(s);
    const isTD = /REC\s*TD|RECTD|TD\s*REC|RECEIVING\s*TD/i.test(s);
    return isRec && !isTargets && !isYards && !isTD;
  });
  add("REC_YDS", "REC YDS", (c) => /REC\s*YD|RECYD|REC\s*YARDS|YDS\s*REC|RECEIVING\s*YARDS/i.test(c));
  add("REC_TD", "REC TD", (c) => /REC\s*TD|RECTD|TD\s*REC|RECEIVING\s*TD/i.test(c));
  // NEW: Kicker + DST outcome chips
  add("K", "K", (c) => /XP[_\s]*MADE|FG[_\s]*MADE/i.test(c));
  add("DST", "DST", (c) => /\bDST\b|DST[_\s]*/i.test(c));
  // Fantasy
  add("DK", "DK", (c) => /\bDK\b|DRAFTKINGS/i.test(c));
  add("FD", "FD", (c) => /\bFD\b|FANDUEL/i.test(c));
  if (!buckets.length && gteCols.length) buckets.push({ key: "all", label: "ALL", cols: gteCols });
  return buckets;
}
const nflLogo = (team) => `/logos/nfl/${String(team || "").toUpperCase()}.png`;
/** ===== PAGE ===== */
function useStableLastUpdated({ dataUrl, metaUrl, artifactEndsWith } = {}) {
  const [d, setD] = React.useState(null);

  React.useEffect(() => {
    let alive = true;
    (async () => {
      // 1) Prefer Last-Modified on the data file itself
      try {
        const h = await fetch(dataUrl, { method: "HEAD", cache: "no-store" });
        const lm = h.headers.get("last-modified");
        if (alive && lm) { setD(new Date(lm)); return; }
      } catch {}

      // 2) Try GET (some hosts omit LM on HEAD)
      try {
        const r = await fetch(`${dataUrl}?_=${Date.now()}`, { cache: "no-store" });
        const lm = r.headers.get("last-modified");
        if (alive && lm) { setD(new Date(lm)); return; }
      } catch {}

      // 3) Fallback: meta.json fields (global or artifact-specific)
      try {
        const m = await fetch(`${metaUrl}?_=${Date.now()}`, { cache: "no-store" }).then(x => x.json());
        let iso = m?.updated_iso || m?.updated_utc || m?.updated || m?.last_updated || null;

        if (!iso && Array.isArray(m?.artifacts) && artifactEndsWith) {
          const art = m.artifacts.find(a => String(a.path || "").endsWith(artifactEndsWith));
          iso = art?.last_updated || art?.tags?.last_updated || null;
        }

        if (alive && iso) {
          const dt = new Date(iso);
          if (!isNaN(dt)) setD(dt);
        }
      } catch {}
    })();
    return () => { alive = false; };
  }, [dataUrl, metaUrl, artifactEndsWith]);

  return d;
}

export default function NflSimulations() {
  const updatedAt = useStableLastUpdated({
    dataUrl: "/data/nfl/classic/latest/playersimulations.json",
    metaUrl: "/data/nfl/classic/latest/meta.json",
    artifactEndsWith: "/data/nfl/classic/latest/playersimulations.json",
  });
  const [kind, setKind] = useState("player"); // player | team | game
  const [view, setView] = useState("outcomes"); // outcomes | projections
  const [oddsMode, setOddsMode] = useState("pct"); // pct | odds
  const [data, setData] = useState({ player: [], team: [], game: [] });
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [q, setQ] = useState("");
  const [sort, setSort] = useState({ key: "", dir: "asc" });
  const [columnsOpen, setColumnsOpen] = useState(false);
  const [visibleByKey, setVisibleByKey] = useState({}); // key -> columns[]
  const [metricTab, setMetricTab] = useState("");
  const [projTab, setProjTab] = useState("ALL");
  useEffect(() => {
    let dead = false;
    (async () => {
      setLoading(true);
      setErr("");
      try {
        const [p, t, g] = await Promise.all([fetchJson(FILES.player), fetchJson(FILES.team), fetchJson(FILES.game)]);
        if (dead) return;
        setData({
          player: Array.isArray(p) ? p : [],
          team: Array.isArray(t) ? t : [],
          game: Array.isArray(g) ? g : [],
        });
      } catch (e) {
        if (!dead) setErr(String(e?.message || e));
      } finally {
        if (!dead) setLoading(false);
      }
    })();
    return () => {
      dead = true;
    };
  }, []);
  const rawRows = data[kind] || [];
  const primaryKey = useMemo(() => pickPrimaryKey(rawRows, kind), [rawRows, kind]);
  const teamKey = useMemo(() => guessTeamKeyFromRow(rawRows?.[0]), [rawRows]);
  const oppKey = useMemo(() => guessOppKeyFromRow(rawRows?.[0]), [rawRows]);
  const slotKey = useMemo(() => guessSlotKeyFromRow(rawRows?.[0]), [rawRows]);
  // Basic cleanup: require the primary key to be present; drop fully empty rows
  const cleanedRows = useMemo(() => {
    const cols = rawRows?.length ? Object.keys(rawRows[0]) : [];
    const checkCols = cols.filter((c) => c !== primaryKey);
    const isAllZeroishRow = (row) => {
      let sawNonZero = false;
      for (const c of checkCols) {
        const v = row?.[c];
        if (v == null || v === "") continue;
        const n = toNum(v);
        if (n != null && Number.isFinite(n)) {
          if (Math.abs(n) > 1e-12) {
            sawNonZero = true;
            break;
          }
        } else {
          sawNonZero = true;
          break;
        }
      }
      return !sawNonZero;
    };
    return (rawRows || [])
      .filter((r) => String(r?.[primaryKey] ?? "").trim().length > 0)
      .filter((r) => !isAllZeroishRow(r));
  }, [rawRows, primaryKey]);
  const allColumns = useMemo(() => {
    const cols = getColumns(cleanedRows);
    return orderColumns(cols, primaryKey);
  }, [cleanedRows, primaryKey]);
  const viewColumns = useMemo(() => {
    if (!allColumns.length) return [];
    if (view === "outcomes") {
      const gte = onlyGteOutcomeCols(allColumns);
      const base = [primaryKey, ...gte];
      return pinAfterPrimary(base, primaryKey, [teamKey, oppKey, slotKey]);
    }
    const projCols = defaultProjectionCols(allColumns, cleanedRows, primaryKey);
    const base = orderColumns(projCols.includes(primaryKey) ? projCols : [primaryKey, ...projCols], primaryKey);
    // Force Team / Opp / Slot immediately after the name col
    return pinAfterPrimary(base, primaryKey, [teamKey, oppKey, slotKey]);
  }, [allColumns, view, primaryKey, cleanedRows, teamKey, oppKey, slotKey]);
  const outcomeBuckets = useMemo(() => {
    if (view !== "outcomes") return [];
    const gteCols = viewColumns.filter((c) => c !== primaryKey);
    return metricBucketsForOutcomes(kind, gteCols);
  }, [view, viewColumns, primaryKey, kind]);
  const projBuckets = useMemo(() => {
    if (view !== "projections") return [];
    return projectionBuckets(viewColumns, primaryKey);
  }, [view, viewColumns, primaryKey]);
  useEffect(() => {
    if (view !== "outcomes") return;
    if (!outcomeBuckets.length) {
      setMetricTab("");
      return;
    }
    setMetricTab((prev) => (outcomeBuckets.some((b) => b.key === prev) ? prev : outcomeBuckets[0].key));
  }, [view, outcomeBuckets]);
  useEffect(() => {
    if (view !== "projections") return;
    if (!projBuckets.length) return;
    setProjTab((prev) => (projBuckets.some((b) => b.key === prev) ? prev : projBuckets[0].key));
  }, [view, projBuckets]);
  const baseColumns = useMemo(() => {
    if (!viewColumns.length) return [];
    if (view === "outcomes" && outcomeBuckets.length && metricTab) {
      const bucket = outcomeBuckets.find((b) => b.key === metricTab);
      return bucket ? [primaryKey, ...bucket.cols] : viewColumns;
    }
    if (view === "projections" && projBuckets.length && projTab) {
      const bucket = projBuckets.find((b) => b.key === projTab);
      return bucket ? [primaryKey, ...bucket.cols] : viewColumns;
    }
    return viewColumns;
  }, [viewColumns, view, outcomeBuckets, metricTab, primaryKey, projBuckets, projTab]);
  const columnsWithData = useMemo(() => {
    return baseColumns.filter((c) => c === primaryKey || hasAnyData(cleanedRows, c, primaryKey));
  }, [baseColumns, cleanedRows, primaryKey]);
  const visKey = `${kind}:${view}:${view === "outcomes" ? metricTab : view === "projections" ? projTab : "proj"}`;
  useEffect(() => {
    if (!columnsWithData.length) return;
    setVisibleByKey((prev) => {
      if (prev[visKey]?.length) return prev;
      return { ...prev, [visKey]: columnsWithData };
    });
    setSort((p) => {
      const defKey = columnsWithData.includes(primaryKey) ? primaryKey : columnsWithData[0] || "";
      return p.key ? p : { key: defKey, dir: "asc" };
    });
  }, [visKey, columnsWithData, primaryKey]);
  const visibleColumns = visibleByKey[visKey] || columnsWithData;
  // Hide rows that are all 0 for the currently visible outcome columns
  const rowsForView = useMemo(() => {
    if (view !== "outcomes") return cleanedRows;
    const statCols = (visibleColumns || []).filter((c) => c !== primaryKey);
    if (!statCols.length) return cleanedRows;
    return (cleanedRows || []).filter((r) => {
      return statCols.some((c) => {
        const n = toNum(r?.[c]);
        return n != null && Number.isFinite(n) && n > 0;
      });
    });
  }, [cleanedRows, view, visibleColumns, primaryKey]);
  const filtered = useMemo(() => {
    const needle = q.trim().toLowerCase();
    if (!needle) return rowsForView;
    return rowsForView.filter((r) =>
      visibleColumns.some((c) => String(r?.[c] ?? "").toLowerCase().includes(needle))
    );
  }, [rowsForView, q, visibleColumns]);
  const sorted = useMemo(() => {
    const { key, dir } = sort;
    if (!key) return filtered;
    const sign = dir === "asc" ? 1 : -1;
    return [...filtered].sort((a, b) => {
      const av = a?.[key];
      const bv = b?.[key];
      const an = toNum(av);
      const bn = toNum(bv);
      if (an != null && bn != null) return (an - bn) * sign;
      return String(av ?? "").localeCompare(String(bv ?? "")) * sign;
    });
  }, [filtered, sort]);
  function onHeaderClick(col) {
    setSort((p) => (p.key === col ? { key: col, dir: p.dir === "asc" ? "desc" : "asc" } : { key: col, dir: "asc" }));
  }
  function toggleColumn(col) {
    setVisibleByKey((prev) => {
      const cur = prev[visKey] || columnsWithData;
      const next = cur.includes(col) ? cur.filter((c) => c !== col) : [...cur, col];
      const enforced = next.includes(primaryKey) ? next : [primaryKey, ...next];
      return { ...prev, [visKey]: enforced };
    });
  }
  const selectAll = () => setVisibleByKey((prev) => ({ ...prev, [visKey]: columnsWithData }));
  const clearAll = () => setVisibleByKey((prev) => ({ ...prev, [visKey]: [primaryKey] }));
  function displayCell(col, v) {
    const n = toNum(v);
    if (view === "outcomes" && col !== primaryKey) {
      if (n == null || n <= 0) return "";
      const p = n <= 1.0000001 ? n : n / 100;
      return oddsMode === "odds" ? probToAmericanOdds(p) : toPct(p);
    }
    if (view === "projections" && col !== primaryKey) {
      if (n == null) return v == null ? "" : String(v);
      if (isProjectionProbCol(col)) return toPct(n);
      return fmt2(n);
    }
    return fmt1(v);
  }
  const title = "NFL Simulations";
  const headerLabel = (c) => {
    if (view === "outcomes" && c !== primaryKey) return prettyOutcomeLabel(c);
    if (view === "projections" && c !== primaryKey) return prettyProjectionLabel(c);
    return c;
  };
  return (
    <div className="min-h-screen bg-slate-950 text-slate-100 px-4 py-5 md:px-6">
      <div className="mx-auto w-full min-w-0 space-y-4">
        {/* Header */}
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div>
            <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
              <h1 className="text-xl font-bold tracking-tight text-slate-100">{title}</h1>
              {updatedAt && (
                <span className="text-xs font-normal italic text-slate-100">
                  Updated: {updatedAt.toLocaleString(undefined, { month: "numeric", day: "numeric", hour: "numeric", minute: "2-digit" })}
                </span>
              )}
            </div>
          </div>
          {view === "outcomes" ? (
            <div className="ml-auto flex items-center gap-1 rounded-full border border-slate-700 bg-slate-900/80 p-1">
              {[
                { key: "pct", label: "%" },
                { key: "odds", label: "Odds" },
              ].map((opt) => (
                <button
                  key={opt.key}
                  onClick={() => setOddsMode(opt.key)}
                  className={cx(
                    "px-4 py-2 text-xs rounded-full transition font-semibold",
                    oddsMode === opt.key
                      ? "bg-sky-500 text-slate-950 shadow-sm"
                      : "text-slate-300 hover:bg-slate-800/80"
                  )}
                >
                  {opt.label}
                </button>
              ))}
            </div>
          ) : null}
        </div>
        {/* Top tabs row */}
        <div className="flex flex-wrap items-center gap-x-4 gap-y-2 rounded-2xl border border-slate-800 bg-slate-900/95 px-3 py-2 shadow-sm">
          {/* Dataset */}
          <div className="flex flex-wrap items-center gap-1">
            {[
              { key: "player", label: "Player Simulations" },
              { key: "team", label: "Team Simulations" },
              { key: "game", label: "Game Simulations" },
            ].map((t) => (
              <button
                key={t.key}
                onClick={() => setKind(t.key)}
                className={cx(
                  "border-b-2 px-3 py-2.5 text-xs transition font-semibold",
                  kind === t.key ? "border-sky-400 text-white" : "border-transparent text-slate-400 hover:text-white"
                )}
              >
                {t.label}
              </button>
            ))}
          </div>
          {/* Outcomes/Projections */}
          <div className="flex flex-wrap items-center gap-1">
            {[
              { key: "outcomes", label: "Outcomes" },
              { key: "projections", label: "Projections" },
            ].map((t) => (
              <button
                key={t.key}
                onClick={() => setView(t.key)}
                className={cx(
                  "border-b-2 px-3 py-2.5 text-xs transition font-semibold",
                  view === t.key ? "border-sky-400 text-white" : "border-transparent text-slate-400 hover:text-white"
                )}
              >
                {t.label}
              </button>
            ))}
          </div>
          {/* Metric tabs (Outcomes only) */}
          {view === "outcomes" && outcomeBuckets.length ? (
            <div className="flex w-full flex-wrap items-center gap-1 border-t border-slate-800 pt-1">
              {outcomeBuckets.map((b) => (
                <button
                  key={b.key}
                  onClick={() => setMetricTab(b.key)}
                  title={
                    ({
                      P_ATT: "Passing Attempts",
                      P_YDS: "Passing Yards",
                      P_TD: "Passing TD",
                      R_ATT: "Rushing Attempts",
                      R_YDS: "Rushing Yards",
                      R_TD: "Rushing TD",
                      REC: "Receptions",
                      REC_YDS: "Receiving Yards",
                      REC_TD: "Receiving TD",
                      DK: "DraftKings",
                      FD: "FanDuel",
                      K: "Kicker",
                      DST: "Defense / DST",
                      all: "All Outcomes",
                    }[b.key] || b.label)
                  }
                  className={cx(
                    "border-b-2 px-3 py-2 text-xs font-semibold transition",
                    metricTab === b.key
                      ? "border-sky-400 text-white"
                      : "border-transparent text-slate-400 hover:text-white"
                  )}
                >
                  {b.label}
                </button>
              ))}
            </div>
          ) : null}
          {/* Projection chips */}
          {view === "projections" && projBuckets.length ? (
            <div className="flex w-full flex-wrap items-center gap-1 border-t border-slate-800 pt-1">
              {projBuckets.map((b) => (
                <button
                  key={b.key}
                  onClick={() => setProjTab(b.key)}
                  className={cx(
                    "border-b-2 px-3 py-2 text-xs font-semibold transition",
                    projTab === b.key
                      ? "border-sky-400 text-white"
                      : "border-transparent text-slate-400 hover:text-white"
                  )}
                >
                  {b.label}
                </button>
              ))}
            </div>
          ) : null}
        </div>
        {/* Controls */}
        <div className="flex flex-wrap items-center gap-3 rounded-2xl border border-slate-800 bg-slate-900/95 p-3 shadow-sm">
          <input
            className="h-8 min-w-0 border border-slate-700 rounded-md px-3 text-xs w-full sm:flex-1 bg-slate-900/70 text-slate-100 placeholder:text-slate-500 focus:outline-none focus:ring-1 focus:ring-sky-500"
            placeholder="Search name / any visible column..."
            value={q}
            onChange={(e) => setQ(e.target.value)}
          />
          <div className="ml-auto flex items-center gap-2">
            <button
              className="h-8 px-3 rounded-md border border-slate-700 bg-slate-900/80 text-xs font-semibold text-slate-100 hover:bg-slate-800 transition"
              onClick={() => setColumnsOpen((v) => !v)}
            >
              Columns
            </button>
            <button
              className="h-8 px-3 rounded-md bg-sky-500 text-slate-950 text-xs font-semibold hover:bg-sky-400 transition border border-sky-400 shadow-sm"
              onClick={() =>
                exportToCsv(
                  `NFL_${kind}_${view}${view === "outcomes" ? "_" + metricTab : view === "projections" ? "_" + projTab : ""}.csv`,
                  sorted,
                  visibleColumns
                )
              }
            >
              Export CSV
            </button>
          </div>
        </div>
        {columnsOpen ? (
          <div className="rounded-2xl border border-slate-800 bg-slate-900/95 shadow-sm p-3">
            <div className="flex items-center justify-between gap-2 flex-wrap mb-2">
              <div className="text-xs text-slate-300">
                Showing <span className="font-semibold text-slate-100">{visibleColumns.length}</span> /{" "}
                <span className="font-semibold text-slate-100">{columnsWithData.length}</span> columns
              </div>
              <div className="flex items-center gap-2">
                <button className="text-xs px-2 py-1 rounded bg-slate-800 hover:bg-slate-700 text-slate-100" onClick={selectAll}>
                  Select all
                </button>
                <button className="text-xs px-2 py-1 rounded bg-slate-800 hover:bg-slate-700 text-slate-100" onClick={clearAll}>
                  Clear
                </button>
                <button className="text-xs px-2 py-1 text-slate-300 hover:text-slate-100 hover:underline" onClick={() => setColumnsOpen(false)}>
                  Close
                </button>
              </div>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-x-6 gap-y-3 max-h-72 overflow-auto pr-2">
              {columnsWithData.map((c) => {
                const checked = visibleColumns.includes(c);
                return (
                  <label
                    key={c}
                    className="flex items-start gap-2 text-[13px] px-1 py-1 rounded hover:bg-slate-800/70 cursor-pointer text-slate-100"
                    title={c}
                  >
                    <input
                      type="checkbox"
                      className="accent-sky-500 mt-0.5 w-3.5 h-3.5 flex-shrink-0"
                      checked={checked}
                      disabled={c === primaryKey}
                      onChange={() => toggleColumn(c)}
                    />
                    <span className="leading-tight whitespace-nowrap">{c === primaryKey ? c : headerLabel(c)}</span>
                  </label>
                );
              })}
            </div>
          </div>
        ) : null}
        {err ? <div className="text-sm text-rose-400">{err}</div> : null}
        {/* TABLE */}
        <div className="rounded-2xl border border-slate-800 bg-slate-900/95 shadow-sm overflow-auto max-h-[70vh]">
          <table className="min-w-max text-xs leading-tight w-full">
            <thead className="bg-slate-900 sticky top-0 z-30">
              <tr>
                {visibleColumns.map((c) => {
                  const isSticky = c === primaryKey;
                  const isSorted = sort.key === c;
                  return (
                    <th
                      key={c}
                      className={cx(
                        "text-center px-2 py-2 font-semibold whitespace-nowrap cursor-pointer select-none text-slate-200",
                        isSticky && "sticky left-0 z-40 bg-slate-900 border-r border-slate-800"
                      )}
                      onClick={() => onHeaderClick(c)}
                    >
                      <div className="inline-flex items-center gap-1">
                        <span>{headerLabel(c)}</span>
                        {isSorted ? <span className="text-[9px] opacity-60">{sort.dir === "asc" ? "▲" : "▼"}</span> : null}
                      </div>
                    </th>
                  );
                })}
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td className="px-2 py-2 text-center text-slate-300" colSpan={visibleColumns.length}>
                    Loading…
                  </td>
                </tr>
              ) : sorted.length === 0 ? (
                <tr>
                  <td className="px-2 py-2 text-center text-slate-300" colSpan={visibleColumns.length}>
                    No rows.
                  </td>
                </tr>
              ) : (
                sorted.map((r, i) => {
                  const rowBg = i % 2 ? "bg-slate-900" : "bg-slate-950";
                  return (
                    <tr key={i} className={rowBg}>
                      {visibleColumns.map((c) => {
                        const isSticky = c === primaryKey;
                        const val = r?.[c];
                        return (
                          <td
                            key={c}
                            className={cx(
                              "px-2 py-1 whitespace-nowrap text-slate-100",
                              c === primaryKey ? "text-left font-semibold" : "text-center",
                              isSticky && `sticky left-0 z-20 ${rowBg} border-r border-slate-800`
                            )}
                          >
                            {c === primaryKey ? (
                              <div className="flex items-center justify-start gap-2 min-w-[220px]">
                                {teamKey ? (
                                  <img
                                    src={nflLogo(r?.[teamKey])}
                                    alt=""
                                    className="w-4 h-4 rounded-sm object-contain flex-shrink-0"
                                    onError={(e) => (e.currentTarget.style.visibility = "hidden")}
                                  />
                                ) : null}
                                <span className="truncate">{displayCell(c, val)}</span>
                              </div>
                            ) : (
                              displayCell(c, val)
                            )}
                          </td>
                        );
                      })}
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
